// Implements: F-UIX-020, F-UIX-007
/**
 * The ratio and the pass or fail state of every pair, as the mockup's "Contraste" card. Pure
 * presentation.
 *
 * Owner round 4: compact rows, a label and a small badge ("AA · 17.85:1"); the 4.5:1 rule is in the
 * (i) next to the title.
 *
 * The state is a WORD in a badge next to the ratio, never a colour alone (F-UIX-007), and the pairs
 * that block the save are named in a sentence below the list. Anything under 4.5:1 fails: the server
 * refuses the palette there, so the interface never calls a lower ratio "good enough for large text".
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Badge } from "../../../shared/ui/atoms/Badge";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { InfoTip } from "../../../shared/ui/molecules/InfoTip";
import { formatRatio } from "./contrastPairs";
import type { PairResult } from "./contrastPairs";
import styles from "./ContrastSummary.module.css";

export interface ContrastSummaryProps {
  readonly pairs: readonly PairResult[];
}

export function ContrastSummary({ pairs }: ContrastSummaryProps): ReactElement {
  const { t } = useTranslation();
  const failing = pairs.filter((pair) => !pair.passes).map((pair) => t(pair.label));

  return (
    <section className={styles.card} aria-labelledby="design-contrast-title">
      <div className={styles.head}>
        <h3 id="design-contrast-title" className={styles.title}>
          {t("design.contrast.title")}
        </h3>
        <InfoTip subject={t("design.contrast.title")} content={t("design.contrast.help")} />
      </div>
      <ul className={styles.pairs}>
        {pairs.map((pair) => (
          <li key={pair.name} className={styles.pair} aria-labelledby={`design-pair-${pair.name}`}>
            <span id={`design-pair-${pair.name}`}>{t(pair.label)}</span>
            <Badge tone={pair.passes ? "success" : "danger"}>
              <Icon name={pair.passes ? "check" : "close"} size="sm" />
              <span>{pair.passes ? t("design.contrast.pass") : t("design.contrast.fail")}</span>
              <span className={styles.ratio}>
                {pair.ratio === null ? t("design.contrast.invalid") : formatRatio(pair.ratio)}
              </span>
            </Badge>
          </li>
        ))}
      </ul>
      {failing.length === 0 ? null : (
        <p className={styles.blocked} role="alert">
          <Icon name="close" size="sm" />
          <span>{`${t("design.contrast.blocked")} ${failing.join(", ")}.`}</span>
        </p>
      )}
    </section>
  );
}
